"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface FeatureItem {
  id: number;
  title: string;
  image: string;
  description: string;
}

interface AccordionFeatureSectionProps {
  features: FeatureItem[];
  heading?: React.ReactNode;
  subheading?: string;
  badge?: string;
  className?: string;
}

const AccordionFeatureSection = ({ 
  features,
  heading,
  subheading,
  badge,
  className
}: AccordionFeatureSectionProps) => {
  const [activeTabId, setActiveTabId] = useState<number | null>(1);
  const [activeImage, setActiveImage] = useState(features[0]?.image || "");

  // Preload all feature images on mount
  useEffect(() => {
    features.forEach((feature) => {
      const img = new Image();
      img.src = feature.image;
    });
  }, [features]);

  return (
    <section className={cn(
      "py-16 md:py-24 relative overflow-hidden",
      className || "bg-gradient-to-br from-emerald-50/40 via-background to-green-50/30 dark:from-emerald-950/20 dark:via-background dark:to-green-950/10"
    )}>
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Animated blur circles */}
        <motion.div 
          className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.2, 0.4]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/3 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"
          animate={{ 
            x: [0, 30, 0],
            y: [0, -20, 0],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
                              linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        {(badge || heading || subheading) && (
          <div className="mb-12 max-w-2xl">
            {badge && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-4"
              >
                <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-primary/10 to-emerald-500/10 text-primary border border-primary/20">
                  {badge}
                </span>
              </motion.div>
            )}
            {heading && (
              <motion.h2 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 tracking-tight"
              >
                {heading}
              </motion.h2>
            )}
            {subheading && (
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-lg text-muted-foreground leading-relaxed"
              >
                {subheading}
              </motion.p>
            )}
          </div>
        )}
        
        {/* Accordion + Image Layout */}
        <div className="flex w-full items-start justify-between gap-12">
          <div className="w-full md:w-1/2">
            <div className="w-full space-y-2">
              {features.map((tab, index) => (
                <div
                  key={tab.id}
                  onMouseEnter={() => {
                    setActiveTabId(tab.id);
                    setActiveImage(tab.image);
                  }}
                  className="border-b border-border/50"
                >
                  {/* Trigger/Header - always visible */}
                  <div className="cursor-pointer py-5 transition group hover:bg-muted/30 rounded-lg px-3 -mx-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-left">
                        <span className={cn(
                          "font-mono text-sm transition-colors duration-300",
                          tab.id === activeTabId 
                            ? "text-primary font-semibold" 
                            : "text-muted-foreground/50"
                        )}>
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <h6 className={cn(
                          "text-xl font-semibold transition-colors duration-300",
                          tab.id === activeTabId 
                            ? "text-foreground" 
                            : "text-muted-foreground group-hover:text-foreground/80"
                        )}>
                          {tab.title}
                        </h6>
                      </div>
                      {/* Chevron indicator */}
                      <motion.div
                        animate={{ rotate: tab.id === activeTabId ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </motion.div>
                    </div>
                  </div>
                  
                  {/* Content - expands/collapses based on hover state */}
                  <AnimatePresence initial={false}>
                    {tab.id === activeTabId && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <p className="pb-4 text-muted-foreground pl-12 whitespace-pre-line">
                          {tab.description}
                        </p>
                        {/* Mobile image */}
                        <div className="mt-4 md:hidden pl-12 pb-4">
                          <img
                            src={tab.image}
                            alt={tab.title}
                            className="h-full max-h-80 w-full rounded-xl object-cover shadow-lg"
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
          
          {/* Enhanced Image Container */}
          <div className="relative m-auto hidden w-1/2 md:block">
            {/* Background Glow */}
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 via-emerald-500/10 to-blue-500/20 rounded-3xl blur-2xl opacity-60" />
            
            {/* Image Container */}
            <div className="relative overflow-hidden rounded-2xl shadow-2xl ring-1 ring-white/10 aspect-[4/3]">
              <AnimatePresence mode="sync" initial={false}>
                <motion.img
                  key={activeImage}
                  src={activeImage}
                  alt="Feature preview"
                  className="absolute inset-0 w-full h-full rounded-2xl object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 1 }}
                  transition={{ duration: 0.15 }}
                />
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { AccordionFeatureSection };
export type { FeatureItem, AccordionFeatureSectionProps };
